/**
 * Whether this browser can install the RasoiX storefront to the Home Screen, and by which route.
 *
 * Pure browser inspection: no React, no storage, no network. Every value is read at call time
 * rather than captured at module load, so a test can vary the environment and so a value that
 * genuinely changes during a session (display mode, after an install) is never stale.
 *
 * This module answers "is there an install path at all". It deliberately does NOT decide whether
 * to show anything — that is the frequency policy in `@/domain/install/install-prompt` plus the
 * hook that combines the two.
 */

/** How the customer would install, when installation is possible at all. */
export type InstallPlatform =
  /** iOS/iPadOS Safari: manual Share → Add to Home Screen. No programmatic install exists. */
  | "ios-safari"
  /** Chromium: `beforeinstallprompt` is expected, and drives a real install dialog. */
  | "chromium"
  /** No install path worth offering — render nothing. */
  | "unsupported";

/** iOS Safari exposes a non-standard `standalone` flag on `navigator`. */
type NavigatorWithStandalone = Navigator & { standalone?: boolean };

/**
 * Chromium's User-Agent Client Hints. `mobile` is the browser's own answer to "is this a phone
 * or tablet", which is exactly the classification needed here and — unlike a pointer or
 * touch-capability query — is not confused by a touchscreen laptop.
 */
type NavigatorWithUserAgentData = Navigator & {
  userAgentData?: { mobile?: boolean };
};

function safeMatchMedia(query: string): boolean {
  try {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(query).matches;
  } catch {
    // Some embedded webviews throw on unusual queries rather than returning a non-match.
    return false;
  }
}

function userAgent(): string {
  try {
    if (typeof navigator === "undefined") return "";
    return navigator.userAgent || "";
  } catch {
    return "";
  }
}

/** True when the document is being displayed as an installed app rather than a browser tab. */
export function isStandaloneDisplayMode(): boolean {
  // `minimal-ui` is deliberately excluded: the manifest asks for `standalone`, and a browser that
  // fell back to minimal-ui still shows browser chrome the customer may want to escape.
  if (safeMatchMedia("(display-mode: standalone)")) return true;
  if (safeMatchMedia("(display-mode: fullscreen)")) return true;

  // iOS predates `display-mode` support and reports only this.
  try {
    if (typeof navigator === "undefined") return false;
    return (navigator as NavigatorWithStandalone).standalone === true;
  } catch {
    return false;
  }
}

/**
 * iOS or iPadOS. iPadOS 13+ reports a desktop Safari user agent, so it is identified by the
 * Mac platform string combined with a touch screen — the standard detection for that case.
 */
export function isIosDevice(): boolean {
  const ua = userAgent();
  if (/iPad|iPhone|iPod/.test(ua)) return true;

  try {
    if (typeof navigator === "undefined") return false;
    const isMacLike = /Macintosh|MacIntel/.test(ua) || navigator.platform === "MacIntel";
    return isMacLike && navigator.maxTouchPoints > 1;
  } catch {
    return false;
  }
}

/**
 * True only for genuine iOS Safari.
 *
 * Add to Home Screen is a Safari feature. Every other iOS browser is a WKWebView wrapper that
 * cannot install — Chrome (`CriOS`), Firefox (`FxiOS`), Edge (`EdgiOS`), Opera (`OPiOS`), and
 * in-app browsers such as Facebook, Instagram, LinkedIn, WeChat and Line. Showing Share-sheet
 * instructions in any of those would send the customer looking for a button that is not there.
 */
export function isIosSafari(): boolean {
  if (!isIosDevice()) return false;

  const ua = userAgent();
  if (/CriOS|FxiOS|EdgiOS|OPiOS|mercury/i.test(ua)) return false;
  if (/FBAN|FBAV|FB_IAB|Instagram|LinkedInApp|MicroMessenger|Line\//i.test(ua)) return false;

  // Real Safari carries both tokens; a bare WKWebView host generally omits `Version/`.
  return /Safari/i.test(ua) && /Version\//i.test(ua);
}

/**
 * True for a Chromium browser running on a phone or tablet.
 *
 * Desktop Chrome and Edge also fire `beforeinstallprompt`, so the event alone would put this card
 * on a laptop; the offer is written for a phone Home Screen on a mobile-first ordering app, and
 * desktop is out of scope for V1.
 *
 * Classification comes from `navigator.userAgentData.mobile` — the browser's own answer, and the
 * reason a pointer or touch-capability query is not used: a touchscreen laptop reports a coarse
 * pointer and would be misread as a phone. The user-agent string is only a fallback for Chromium
 * builds predating Client Hints, where Android is the sole installable mobile Chromium.
 */
export function isMobileChromium(): boolean {
  try {
    if (typeof navigator === "undefined") return false;

    const uaData = (navigator as NavigatorWithUserAgentData).userAgentData;
    if (uaData && typeof uaData.mobile === "boolean") return uaData.mobile;
  } catch {
    // Fall through to the user-agent string.
  }

  const ua = userAgent();
  // An Android WebView (`; wv`) hosts in-app browsers, which never fire the install event.
  if (/\bwv\b/.test(ua)) return false;
  return /Android/i.test(ua);
}

/**
 * The install route available right now.
 *
 * Returns `unsupported` while running as an installed app: callers should test
 * {@link isStandaloneDisplayMode} separately, because "already installed" and "cannot install"
 * are different answers that the UI must not conflate.
 */
export function resolveInstallPlatform(): InstallPlatform {
  if (typeof window === "undefined") return "unsupported";
  if (isIosSafari()) return "ios-safari";
  // Every other iOS browser is a WKWebView that cannot install. iOS must be excluded before the
  // Chromium check, because Chrome on iOS reports itself as mobile Chromium yet cannot install.
  if (isIosDevice()) return "unsupported";
  if (isMobileChromium()) return "chromium";
  return "unsupported";
}
